/*
	Header component with user profile and logout functionality
	Includes a breadcrumb for navigation context
	Uses react-router-dom for navigation and FontAwesome for icons
*/

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Breadcrumb from './Breadcrumb';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faPen, faSignOutAlt } from '@fortawesome/free-solid-svg-icons';

// Header component
const Header: React.FC = () => {
	// Retrieve user information from localStorage
	const userName = localStorage.getItem('name') || '';
	const userFirstName = localStorage.getItem('firstName') || '';

	// State for dropdown menu
	const navigate = useNavigate();
	const [dropdownOpen, setDropdownOpen] = useState(false);
	const dropdownRef = useRef<HTMLDivElement>(null);

	// Handle user logout
	const handleLogout = () => {
		localStorage.removeItem('token');
		localStorage.removeItem('email');
		localStorage.removeItem('name');
		localStorage.removeItem('firstName');
		window.location.href = '/login';
	};

	// Navigate to edit profile page
	const handleEditProfile = () => {
		const userId = localStorage.getItem('userId');
		if (userId) navigate(`/app/users/${userId}/edit`);
		else alert('Impossible de récupérer votre profil.');
		setDropdownOpen(false);
	};

	// Close the menu if clicked outside
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				dropdownRef.current &&
				!dropdownRef.current.contains(event.target as Node)
			) {
				setDropdownOpen(false);
			}
		};
		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);

	// Render the header with breadcrumb and user menu
	return (
		<header className="flex justify-between items-center bg-white shadow-md px-6 py-3 border-b border-gray-200">
			<Breadcrumb />
			<div className="relative flex items-center space-x-4" ref={dropdownRef}>
				<button
					onClick={() => setDropdownOpen(!dropdownOpen)}
					className="flex items-center gap-2 bg-gray-100 hover:bg-gray-200 px-3 py-1 rounded-lg border border-gray-300 shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-400"
				>
					<FontAwesomeIcon icon={faUser} className="w-4 h-4 text-gray-700" />
					<span className="font-medium text-gray-700">
						{userFirstName} {userName}
					</span>
				</button>

				{dropdownOpen && (
					<div className="absolute right-0 top-full mt-1 min-w-max bg-white border rounded shadow-lg z-50">
						<button
							onClick={handleEditProfile}
							className="flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-gray-100 transition-colors"
						>
							<FontAwesomeIcon icon={faPen} className="w-4 h-4 text-gray-500" />
							Modifier mon profil
						</button>
						<button
							onClick={handleLogout}
							className="flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-gray-100 transition-colors w-full"
						>
							<FontAwesomeIcon
								icon={faSignOutAlt}
								className="w-4 h-4 text-gray-500"
							/>
							Déconnexion
						</button>
					</div>
				)}
			</div>
		</header>
	);
};

// Export the component
export default Header;
